<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { cortexUsageAPI } from 'dashboard/api/cortex';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import {
  BaseTable,
  BaseTableCell,
  BaseTableRow,
} from 'dashboard/components-next/table';
import CortexSectionGuide from './CortexSectionGuide.vue';

const { t } = useI18n();

const isLoading = ref(false);
const usage = ref(null);
const selectedDays = ref(30);
const activeSection = ref('overview');
const recentEventsPagination = ref({ page: 1, per_page: 15, total: 0 });

const dayOptions = computed(() => [
  { value: 7, label: t('CORTEX.COSTS.PERIOD.SEVEN_DAYS') },
  { value: 30, label: t('CORTEX.COSTS.PERIOD.THIRTY_DAYS') },
  { value: 90, label: t('CORTEX.COSTS.PERIOD.NINETY_DAYS') },
]);

const sections = computed(() => [
  {
    key: 'overview',
    icon: 'i-lucide-chart-no-axes-combined',
    label: t('CORTEX.COSTS.SECTIONS.OVERVIEW'),
    description: t('CORTEX.COSTS.ESTIMATED_NOTE'),
  },
  {
    key: 'knowledge',
    icon: 'i-lucide-database',
    label: t('CORTEX.COSTS.KNOWLEDGE.TITLE'),
    description: t('CORTEX.COSTS.KNOWLEDGE.DESCRIPTION'),
  },
  {
    key: 'messages',
    icon: 'i-lucide-messages-square',
    label: t('CORTEX.COSTS.MESSAGES.TITLE'),
    description: t('CORTEX.COSTS.MESSAGES.DESCRIPTION'),
  },
  {
    key: 'events',
    icon: 'i-lucide-receipt-text',
    label: t('CORTEX.COSTS.RECENT_EVENTS'),
    description: t('CORTEX.COSTS.RECENT_EVENTS_DESCRIPTION'),
  },
]);

const totals = computed(
  () =>
    usage.value?.totals || {
      input_tokens: 0,
      output_tokens: 0,
      cached_input_tokens: 0,
      cache_write_input_tokens: 0,
      cost_usd: 0,
      events_count: 0,
    }
);
const knowledgeTotals = computed(
  () =>
    usage.value?.knowledge?.totals || {
      knowledge_bases_count: 0,
      entries_count: 0,
      published_entries_count: 0,
      draft_entries_count: 0,
      chunks_count: 0,
      embedded_chunks_count: 0,
      content_characters: 0,
      estimated_tokens: 0,
    }
);
const messageTotals = computed(
  () =>
    usage.value?.messages?.totals || {
      messages_count: 0,
      incoming_messages_count: 0,
      outgoing_messages_count: 0,
      questions_count: 0,
      answers_count: 0,
      normalized_messages_count: 0,
      normalized_content_characters: 0,
      normalized_estimated_tokens: 0,
      media_conversions_count: 0,
      media_conversions_completed_count: 0,
    }
);
const recentEvents = computed(() => usage.value?.recent_events || []);
const recentEventHeaders = computed(() => [
  t('CORTEX.COSTS.TABLE.TYPE'),
  t('CORTEX.COSTS.TABLE.MODEL'),
  t('CORTEX.COSTS.TABLE.SOURCE'),
  t('CORTEX.COSTS.TABLE.TOKENS'),
  t('CORTEX.COSTS.TABLE.COST'),
]);

const formatNumber = value => Number(value || 0).toLocaleString();
const formatCurrency = value =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 6,
  }).format(Number(value || 0));

const credentialSourceLabel = source => {
  if (source === 'account_key') {
    return t('CORTEX.COSTS.CREDENTIAL_SOURCE.account_key');
  }
  return t('CORTEX.COSTS.CREDENTIAL_SOURCE.platform_key');
};
const credentialLabel = credential =>
  credential.credential_name ||
  credential.connection_name ||
  credentialSourceLabel(credential.credential_source);
const eventCredentialLabel = event =>
  event.credential_name ||
  event.credential_connection_name ||
  credentialSourceLabel(event.credential_source);
const knowledgeBaseStatusLabel = status =>
  status === 'active'
    ? t('CORTEX.KNOWLEDGE.STATUS.active')
    : t('CORTEX.KNOWLEDGE.STATUS.inactive');
const knowledgeBaseStatusColor = status =>
  status === 'active' ? 'teal' : 'slate';
const eventTypeLabel = eventType => {
  if (eventType === 'llm_response')
    return t('CORTEX.COSTS.EVENT_TYPE.llm_response');
  if (eventType === 'llm_tool_planning')
    return t('CORTEX.COSTS.EVENT_TYPE.llm_tool_planning');
  if (eventType === 'qa_generation')
    return t('CORTEX.COSTS.EVENT_TYPE.qa_generation');
  if (eventType === 'guardrail') return t('CORTEX.COSTS.EVENT_TYPE.guardrail');
  if (eventType === 'conversation_summary')
    return t('CORTEX.COSTS.EVENT_TYPE.conversation_summary');
  return t('CORTEX.COSTS.EVENT_TYPE.embedding');
};

const fetchUsage = async (page = recentEventsPagination.value.page) => {
  isLoading.value = true;
  try {
    const { data } = await cortexUsageAPI.get({
      days: selectedDays.value,
      page,
      per_page: recentEventsPagination.value.per_page,
    });
    usage.value = data;
    recentEventsPagination.value = data.recent_events_pagination;
  } catch (error) {
    useAlert(t('CORTEX.COSTS.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const changePeriod = () => fetchUsage(1);
const changePage = page => fetchUsage(page);
const changePerPage = perPage => {
  recentEventsPagination.value.per_page = perPage;
  fetchUsage(1);
};
const selectSection = section => {
  activeSection.value = section.key;
};

onMounted(fetchUsage);
</script>

<template>
  <CortexSectionGuide
    :title="t('CORTEX.COSTS.TITLE')"
    :description="t('CORTEX.COSTS.DESCRIPTION')"
    :sections="sections"
    :active-section="activeSection"
    @section-changed="selectSection"
  >
    <div class="grid gap-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <p class="text-sm text-n-slate-11">
          {{ t('CORTEX.COSTS.ESTIMATED_NOTE') }}
        </p>
        <div class="w-full sm:w-56">
          <AdaptiveSelect
            v-model="selectedDays"
            :options="dayOptions"
            @update:model-value="changePeriod"
          />
        </div>
      </div>

      <woot-loading-state v-if="isLoading" />

      <template v-else>
        <div
          v-show="activeSection === 'overview'"
          class="grid gap-4"
          data-testid="cortex-costs-overview"
        >
          <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.METRICS.COST') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatCurrency(totals.cost_usd) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.METRICS.INPUT_TOKENS') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatNumber(totals.input_tokens) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.METRICS.OUTPUT_TOKENS') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatNumber(totals.output_tokens) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.METRICS.EVENTS') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatNumber(totals.events_count) }}
              </p>
            </div>
          </div>

          <div class="grid gap-4 lg:grid-cols-2">
            <section class="rounded-lg border border-n-weak">
              <div class="border-b border-n-weak px-4 py-3">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ t('CORTEX.COSTS.BY_CREDENTIAL') }}
                </h3>
              </div>
              <div class="divide-y divide-n-weak">
                <div
                  v-for="row in usage?.by_credential || []"
                  :key="`${row.connection_id || 'legacy'}-${row.credential_source}`"
                  class="flex items-center justify-between gap-3 px-4 py-3"
                >
                  <span class="text-sm text-n-slate-12">
                    {{ credentialLabel(row) }}
                  </span>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ formatCurrency(row.cost_usd) }}
                  </span>
                </div>
                <p
                  v-if="!usage?.by_credential?.length"
                  class="px-4 py-3 text-sm text-n-slate-11"
                >
                  {{ t('CORTEX.COSTS.EMPTY') }}
                </p>
              </div>
            </section>

            <section class="rounded-lg border border-n-weak">
              <div class="border-b border-n-weak px-4 py-3">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ t('CORTEX.COSTS.BY_MODEL') }}
                </h3>
              </div>
              <div class="divide-y divide-n-weak">
                <div
                  v-for="row in usage?.by_provider_model || []"
                  :key="`${row.provider}-${row.model}`"
                  class="flex items-center justify-between gap-3 px-4 py-3"
                >
                  <span class="min-w-0 text-sm text-n-slate-12">
                    <span class="block truncate">{{ row.model }}</span>
                    <span class="text-xs text-n-slate-11">
                      {{ row.provider }}
                    </span>
                  </span>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ formatCurrency(row.cost_usd) }}
                  </span>
                </div>
                <p
                  v-if="!usage?.by_provider_model?.length"
                  class="px-4 py-3 text-sm text-n-slate-11"
                >
                  {{ t('CORTEX.COSTS.EMPTY') }}
                </p>
              </div>
            </section>
          </div>
        </div>

        <section
          v-show="activeSection === 'knowledge'"
          class="rounded-lg border border-n-weak"
          data-testid="cortex-costs-knowledge"
        >
          <div class="border-b border-n-weak px-4 py-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('CORTEX.COSTS.KNOWLEDGE.TITLE') }}
            </h3>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ t('CORTEX.COSTS.KNOWLEDGE.DESCRIPTION') }}
            </p>
          </div>
          <div class="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.KNOWLEDGE.METRICS.BASES') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatNumber(knowledgeTotals.knowledge_bases_count) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.KNOWLEDGE.METRICS.ENTRIES') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatNumber(knowledgeTotals.entries_count) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.KNOWLEDGE.METRICS.CHUNKS') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatNumber(knowledgeTotals.chunks_count) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.KNOWLEDGE.METRICS.TOKENS') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatNumber(knowledgeTotals.estimated_tokens) }}
              </p>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-n-weak text-sm">
              <thead class="bg-n-alpha-2 text-left text-xs text-n-slate-11">
                <tr>
                  <th class="px-4 py-2 font-medium">
                    {{ t('CORTEX.COSTS.KNOWLEDGE.TABLE.BASE') }}
                  </th>
                  <th class="px-4 py-2 font-medium">
                    {{ t('CORTEX.COSTS.KNOWLEDGE.TABLE.ENTRIES') }}
                  </th>
                  <th class="px-4 py-2 font-medium">
                    {{ t('CORTEX.COSTS.KNOWLEDGE.TABLE.CHUNKS') }}
                  </th>
                  <th class="px-4 py-2 font-medium">
                    {{ t('CORTEX.COSTS.KNOWLEDGE.TABLE.EMBEDDED') }}
                  </th>
                  <th class="px-4 py-2 font-medium">
                    {{ t('CORTEX.COSTS.KNOWLEDGE.TABLE.TOKENS') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr
                  v-for="row in usage?.knowledge?.by_knowledge_base || []"
                  :key="row.id"
                >
                  <td class="px-4 py-2 text-n-slate-12">
                    <span class="block font-medium">{{ row.name }}</span>
                    <Label
                      class="mt-1"
                      :label="knowledgeBaseStatusLabel(row.status)"
                      :color="knowledgeBaseStatusColor(row.status)"
                      compact
                    >
                      <template #icon>
                        <span class="i-lucide-circle size-2 fill-current" />
                      </template>
                    </Label>
                  </td>
                  <td class="px-4 py-2 text-n-slate-12">
                    {{ formatNumber(row.entries_count) }}
                  </td>
                  <td class="px-4 py-2 text-n-slate-12">
                    {{ formatNumber(row.chunks_count) }}
                  </td>
                  <td class="px-4 py-2 text-n-slate-12">
                    {{ formatNumber(row.embedded_chunks_count) }}
                  </td>
                  <td class="px-4 py-2 text-n-slate-12">
                    {{ formatNumber(row.estimated_tokens) }}
                  </td>
                </tr>
                <tr v-if="!usage?.knowledge?.by_knowledge_base?.length">
                  <td colspan="5" class="px-4 py-3 text-sm text-n-slate-11">
                    {{ t('CORTEX.COSTS.KNOWLEDGE.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section
          v-show="activeSection === 'messages'"
          class="rounded-lg border border-n-weak"
          data-testid="cortex-costs-messages"
        >
          <div class="border-b border-n-weak px-4 py-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('CORTEX.COSTS.MESSAGES.TITLE') }}
            </h3>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ t('CORTEX.COSTS.MESSAGES.DESCRIPTION') }}
            </p>
          </div>
          <div class="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.MESSAGES.METRICS.MESSAGES') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatNumber(messageTotals.messages_count) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.MESSAGES.METRICS.QUESTIONS') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatNumber(messageTotals.questions_count) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.MESSAGES.METRICS.ANSWERS') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatNumber(messageTotals.answers_count) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.MESSAGES.METRICS.NORMALIZED') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatNumber(messageTotals.normalized_messages_count) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.MESSAGES.METRICS.TOKENS') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{ formatNumber(messageTotals.normalized_estimated_tokens) }}
              </p>
            </div>
            <div class="rounded-lg border border-n-weak p-3">
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('CORTEX.COSTS.MESSAGES.METRICS.CONVERSIONS') }}
              </p>
              <p class="mt-1 text-xl font-semibold text-n-slate-12">
                {{
                  formatNumber(messageTotals.media_conversions_completed_count)
                }}
              </p>
            </div>
          </div>
        </section>

        <section
          v-show="activeSection === 'events'"
          class="rounded-lg border border-n-weak"
          data-testid="cortex-costs-events"
        >
          <div class="border-b border-n-weak px-4 py-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('CORTEX.COSTS.RECENT_EVENTS') }}
            </h3>
          </div>
          <BaseTable
            :headers="recentEventHeaders"
            :items="recentEvents"
            :no-data-message="t('CORTEX.COSTS.EMPTY')"
          >
            <template #row="{ items }">
              <BaseTableRow
                v-for="event in items"
                :key="event.id"
                :item="event"
              >
                <BaseTableCell>
                  {{ eventTypeLabel(event.event_type) }}
                </BaseTableCell>
                <BaseTableCell>{{ event.model }}</BaseTableCell>
                <BaseTableCell>{{ eventCredentialLabel(event) }}</BaseTableCell>
                <BaseTableCell>
                  {{
                    formatNumber(
                      event.input_tokens +
                        event.output_tokens +
                        event.cached_input_tokens +
                        event.cache_write_input_tokens
                    )
                  }}
                </BaseTableCell>
                <BaseTableCell align="end" class="font-medium">
                  {{ formatCurrency(event.cost_usd) }}
                </BaseTableCell>
              </BaseTableRow>
            </template>
          </BaseTable>
          <PaginationFooter
            v-if="recentEventsPagination.total"
            :current-page="recentEventsPagination.page"
            :total-items="recentEventsPagination.total"
            :items-per-page="recentEventsPagination.per_page"
            show-page-size-selector
            @update:current-page="changePage"
            @update:items-per-page="changePerPage"
          />
        </section>
      </template>
    </div>
  </CortexSectionGuide>
</template>
